import { useState, useCallback, useRef, useEffect } from 'react';
import { Header, Page, Content } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import GetAppIcon from '@material-ui/icons/GetApp';
import StopIcon from '@material-ui/icons/Stop';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import DeleteIcon from '@material-ui/icons/Delete';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { SyncForm, SyncFormValues, DEFAULT_FORM_VALUES } from '../../../components/catalog/SyncForm';
import type { AIProviderOption, GroupOption } from '../../../components/catalog/SyncForm';
import { SyncProgressLog } from '../../../components/catalog/SyncProgressLog';
import type { SyncSummary } from '../../../components/catalog/SyncProgressLog';
import { LoadDependenciesButton } from './LoadDependenciesButton';
import type { LoadDependenciesResult, LoadGroupDependenciesResult, ProjectDependencies, EnvVariable } from './LoadDependenciesButton';

interface CatalogOption {
  ref: string;
  name: string;
  type: string;
}

type DependencySource = 'component' | 'resource';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
  },
  formPaper: {
    padding: theme.spacing(3),
  },
  progressPaper: {
    padding: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  envPaper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
  envRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  envLabel: {
    fontFamily: 'monospace',
    fontWeight: 600,
    minWidth: 200,
    fontSize: '0.875rem',
  },
  projectHeader: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

type JobStatus = 'idle' | 'starting' | 'running' | 'completed' | 'failed' | 'cancelled';

interface JobPollData {
  id: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  progress: { processed: number; total: number; currentProject?: string };
  summary: SyncSummary;
  createdAt: number;
  finishedAt?: number;
}

const DEFAULT_PROVIDERS: AIProviderOption[] = [
  { id: 'openai', name: 'OpenAI (ChatGPT)', enabled: true },
];

const POLL_INTERVAL_MS = 2000;

export function SyncPage() {
  const classes = useStyles();
  const [values, setValues] = useState<SyncFormValues>(DEFAULT_FORM_VALUES);
  const [jobStatus, setJobStatus] = useState<JobStatus>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ processed: number; total: number }>({ processed: 0, total: 0 });
  const [logLines, setLogLines] = useState<string[]>([]);
  const [summary, setSummary] = useState<SyncSummary | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastLogCountRef = useRef<number>(0);

  const [envVars, setEnvVars] = useState<EnvVariable[]>([]);
  const [envValues, setEnvValues] = useState<Record<string, string>>({});
  const [envDepSource, setEnvDepSource] = useState<Record<string, DependencySource>>({});
  const [envRelatedResources, setEnvRelatedResources] = useState<Record<string, string[]>>({});
  const [envError, setEnvError] = useState<string | null>(null);
  const [envProject, setEnvProject] = useState<string | null>(null);

  // Group mode: per-project dependencies
  const [groupProjects, setGroupProjects] = useState<ProjectDependencies[]>([]);
  const [groupValues, setGroupValues] = useState<Record<string, Record<string, string>>>({});
  const [groupDepSource, setGroupDepSource] = useState<Record<string, Record<string, DependencySource>>>({});
  const [groupRelatedResources, setGroupRelatedResources] = useState<Record<string, Record<string, string[]>>>({});
  const [groupName, setGroupName] = useState<string | null>(null);

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [resources, setResources] = useState<CatalogOption[]>([]);
  const [componentOptions, setComponentOptions] = useState<CatalogOption[]>([]);
  const [componentLoading, setComponentLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    catalogApi
      .getEntities({ filter: { kind: 'Group' }, fields: ['metadata.name', 'metadata.namespace'] })
      .then(response => {
        const opts: GroupOption[] = response.items.map(entity => {
          const ns = entity.metadata.namespace ?? 'default';
          const name = entity.metadata.name;
          return { ref: `group:${ns}/${name}`, name };
        });
        opts.sort((a, b) => a.name.localeCompare(b.name));
        setGroups(opts);
      })
      .catch(() => {});

    catalogApi
      .getEntities({ filter: { kind: 'Resource' }, fields: ['metadata.name', 'metadata.namespace', 'metadata.title', 'spec.type'] })
      .then(response => {
        const opts: CatalogOption[] = response.items.map(entity => {
          const ns = entity.metadata.namespace ?? 'default';
          const name = entity.metadata.name;
          const title = entity.metadata.title ?? name;
          const type = (entity.spec as any)?.type ?? '';
          return { ref: `resource:${ns}/${name}`, name: title, type };
        });
        opts.sort((a, b) => a.name.localeCompare(b.name));
        setResources(opts);
      })
      .catch(() => {});
  }, [catalogApi]);

  // Busca componentes com debounce enquanto o usuário digita
  const searchComponents = useCallback((query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!query || query.length < 2) {
      setComponentOptions([]);
      return;
    }

    setComponentLoading(true);
    searchTimerRef.current = setTimeout(() => {
      catalogApi
        .getEntities({
          filter: { kind: 'Component' },
          fields: ['metadata.name', 'metadata.namespace', 'metadata.title', 'spec.type'],
        })
        .then(response => {
          const queryLower = query.toLowerCase();
          const opts: CatalogOption[] = response.items
            .filter(entity => {
              const name = entity.metadata.name.toLowerCase();
              const title = (entity.metadata.title ?? '').toLowerCase();
              return name.includes(queryLower) || title.includes(queryLower);
            })
            .map(entity => {
              const ns = entity.metadata.namespace ?? 'default';
              const name = entity.metadata.name;
              const title = entity.metadata.title ?? name;
              const type = (entity.spec as any)?.type ?? '';
              return { ref: `component:${ns}/${name}`, name: title, type };
            });
          opts.sort((a, b) => a.name.localeCompare(b.name));
          setComponentOptions(opts);
        })
        .catch(() => setComponentOptions([]))
        .finally(() => setComponentLoading(false));
    }, 400);
  }, [catalogApi]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const startPolling = useCallback((id: string) => {
    lastLogCountRef.current = 0;

    const poll = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('gitlab-catalog-sync');
        const res = await fetchApi.fetch(`${baseUrl}/jobs/${id}`);
        if (!res.ok) return;
        const data: JobPollData = await res.json();

        setProgress({ processed: data.progress.processed, total: data.progress.total });
        setSummary(data.summary);

        const logsRes = await fetchApi.fetch(`${baseUrl}/jobs/${id}/logs`);
        if (logsRes.ok) {
          const logsData = await logsRes.json();
          const allLogs: Array<{ timestamp: string; level: string; message: string }> = logsData.logs ?? [];
          const formatted = allLogs.map(
            l => `[${l.level.toUpperCase()}] ${l.message}`,
          );
          setLogLines(formatted);
          lastLogCountRef.current = allLogs.length;
        }

        if (data.status !== 'running') {
          setJobStatus(data.status);
          stopPolling();
        }
      } catch {
        // polling error, will retry next interval
      }
    };

    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    poll();
  }, [discoveryApi, fetchApi, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleExecute = useCallback(async () => {
    setJobStatus('starting');
    setLogLines([]);
    setSummary(null);
    setProgress({ processed: 0, total: 0 });
    setElapsedMs(0);
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 1000);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('gitlab-catalog-sync');
      const response = await fetchApi.fetch(`${baseUrl}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(values.syncMode === 'project'
            ? { gitlabProject: values.gitlabProject }
            : { gitlabGroup: values.gitlabGroup }),
          defaultBranch: values.defaultBranch,
          useFallbackBranch: values.useFallbackBranch,
          fallbackBranchPattern: values.fallbackBranchPattern,
          fallbackCaseSensitive: values.fallbackCaseSensitive,
          createCatalogInfo: values.createCatalogInfo,
          overwriteCatalogInfo: values.overwriteCatalogInfo,
          owner: values.owner,
          openMR: values.openMR,
          enrichWithAI: values.enrichWithAI,
          aiProvider: values.aiProvider,
          extraDependsOn: groupProjects.length > 0
            ? Object.fromEntries(
                groupProjects.map(p => [
                  p.project.path_with_namespace,
                  Object.values(groupValues[p.project.path_with_namespace] ?? {}).filter(Boolean),
                ]).filter(([, deps]) => (deps as string[]).length > 0)
              )
            : Object.values(envValues).filter(Boolean),
        }),
      });

      if (!response.ok) {
        const errBody = await response.text();
        setLogLines([`Erro: ${response.status} - ${errBody}`]);
        setJobStatus('failed');
        stopPolling();
        return;
      }

      const data = await response.json();
      setJobId(data.jobId);
      setProgress({ processed: 0, total: data.totalProjects });
      setJobStatus('running');
      startPolling(data.jobId);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setLogLines([`Erro: ${message}`]);
      setJobStatus('failed');
      stopPolling();
    }
  }, [values, discoveryApi, fetchApi, startPolling, stopPolling]);

  const handleCancel = useCallback(async () => {
    if (!jobId) return;
    try {
      const baseUrl = await discoveryApi.getBaseUrl('gitlab-catalog-sync');
      await fetchApi.fetch(`${baseUrl}/jobs/${jobId}/cancel`, { method: 'POST' });
    } catch {
      // best effort
    }
  }, [jobId, discoveryApi, fetchApi]);

  const handleDownloadLogs = useCallback(async () => {
    if (!jobId) return;
    try {
      const baseUrl = await discoveryApi.getBaseUrl('gitlab-catalog-sync');
      const res = await fetchApi.fetch(`${baseUrl}/jobs/${jobId}/logs?format=text`);
      if (!res.ok) return;
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sync-${jobId}.log`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // best effort
    }
  }, [jobId, discoveryApi, fetchApi]);

  const handleDepsLoaded = useCallback((result: LoadDependenciesResult) => {
    setEnvError(null);
    setEnvProject(result.project.path_with_namespace);
    setEnvVars(result.envVars);
    const initial: Record<string, string> = {};
    const initialSource: Record<string, DependencySource> = {};
    result.envVars.forEach(v => {
      // Se há sugestão inferida, pré-preenche com ela
      initial[v.key] = result.suggestions[v.key] ?? '';
      initialSource[v.key] = 'resource';
    });
    setEnvValues(initial);
    setEnvDepSource(initialSource);
    setEnvRelatedResources(result.relatedResources);
    // Clear group state
    setGroupProjects([]);
    setGroupValues({});
    setGroupDepSource({});
    setGroupRelatedResources({});
    setGroupName(null);
  }, []);

  const handleGroupDepsLoaded = useCallback((result: LoadGroupDependenciesResult) => {
    setEnvError(null);
    setGroupName(result.group);
    setGroupProjects(result.projects);

    const vals: Record<string, Record<string, string>> = {};
    const sources: Record<string, Record<string, DependencySource>> = {};
    const related: Record<string, Record<string, string[]>> = {};

    for (const p of result.projects) {
      const projectKey = p.project.path_with_namespace;
      vals[projectKey] = {};
      sources[projectKey] = {};
      related[projectKey] = p.relatedResources;

      for (const v of p.envVars) {
        vals[projectKey][v.key] = p.suggestions[v.key] ?? '';
        sources[projectKey][v.key] = 'resource';
      }
    }

    setGroupValues(vals);
    setGroupDepSource(sources);
    setGroupRelatedResources(related);
    // Clear single project state
    setEnvVars([]);
    setEnvValues({});
    setEnvDepSource({});
    setEnvRelatedResources({});
    setEnvProject(null);
  }, []);

  const handleDepsError = useCallback((message: string) => {
    setEnvError(message);
    setEnvVars([]);
    setEnvValues({});
    setEnvProject(null);
    setGroupProjects([]);
    setGroupValues({});
    setGroupDepSource({});
    setGroupRelatedResources({});
    setGroupName(null);
  }, []);

  const handleReset = useCallback(() => {
    stopPolling();
    setLogLines([]);
    setSummary(null);
    setJobId(null);
    setJobStatus('idle');
    setValues(DEFAULT_FORM_VALUES);
    setProgress({ processed: 0, total: 0 });
    setElapsedMs(0);
  }, [stopPolling]);

  const isRunning = jobStatus === 'running' || jobStatus === 'starting';
  const isFinished = jobStatus === 'completed' || jobStatus === 'failed' || jobStatus === 'cancelled';
  const progressPct = progress.total > 0 ? (progress.processed / progress.total) * 100 : 0;

  const formatElapsed = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <Page themeId="tool">
      <Header title="GitLab Catalog Sync" subtitle="Sincronize projetos do GitLab com o catálogo do Backstage" />
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper className={classes.formPaper}>
              <Typography variant="h6" gutterBottom>
                Configuração
              </Typography>
              <SyncForm
                values={values}
                onChange={setValues}
                providers={DEFAULT_PROVIDERS}
                groups={groups}
                disabled={isRunning}
              />
              <div className={classes.actions}>
                <LoadDependenciesButton
                  projectPath={values.syncMode === 'project' ? values.gitlabProject : values.gitlabGroup}
                  branch={values.defaultBranch}
                  mode={values.syncMode === 'project' ? 'project' : 'group'}
                  disabled={isRunning}
                  onLoaded={handleDepsLoaded}
                  onGroupLoaded={handleGroupDepsLoaded}
                  onError={handleDepsError}
                />
                {isRunning && (
                  <Button
                    onClick={handleCancel}
                    startIcon={<StopIcon />}
                    color="secondary"
                    variant="outlined"
                  >
                    Cancelar
                  </Button>
                )}
                {isFinished && (
                  <Button onClick={handleReset} variant="outlined">
                    Nova Sincronização
                  </Button>
                )}
                {!isRunning && (
                  <Button
                    onClick={handleExecute}
                    color="primary"
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    disabled={isRunning || (values.syncMode === 'group' ? !values.gitlabGroup.trim() : !values.gitlabProject.trim())}
                  >
                    Executar Sync
                  </Button>
                )}
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper className={classes.progressPaper}>
              <Typography variant="h6" gutterBottom>
                Progresso
              </Typography>

              {isRunning && (
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      {progress.processed} de {progress.total} projetos
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatElapsed(elapsedMs)}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={progressPct} />
                </Box>
              )}

              {isFinished && summary && (
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    Concluído em {formatElapsed(elapsedMs)}
                    {jobStatus === 'cancelled' && ' (cancelado)'}
                    {jobStatus === 'failed' && ' (falhou)'}
                  </Typography>
                  {logLines.length > 0 && (
                    <Button onClick={handleDownloadLogs} startIcon={<GetAppIcon />} size="small">
                      Baixar Logs
                    </Button>
                  )}
                </Box>
              )}

              <SyncProgressLog lines={logLines} summary={isFinished ? summary : null} />

              {jobStatus === 'idle' && (
                <Typography variant="body2" color="textSecondary">
                  Configure as opções e clique em "Executar Sync" para iniciar.
                </Typography>
              )}
            </Paper>

            {envError && (
              <Box mt={2}>
                <Alert severity="error">{envError}</Alert>
              </Box>
            )}

            {envVars.length > 0 && (
              <Paper className={classes.envPaper}>
                <Typography variant="h6" gutterBottom>
                  Dependências — {envProject}
                </Typography>
                <Box mt={2}>
                  {envVars.map(v => {
                    const source = envDepSource[v.key] ?? 'component';
                    const options = source === 'resource' ? resources : componentOptions;
                    return (
                      <div key={v.key} className={classes.envRow}>
                        <Typography className={classes.envLabel}>{v.key}</Typography>
                        <ToggleButtonGroup
                          value={source}
                          exclusive
                          onChange={(_e, val) => {
                            if (val) {
                              setEnvDepSource(prev => ({ ...prev, [v.key]: val }));
                              setEnvValues(prev => ({ ...prev, [v.key]: '' }));
                            }
                          }}
                          size="small"
                        >
                          <ToggleButton value="component">Comp</ToggleButton>
                          <ToggleButton value="resource">Res</ToggleButton>
                        </ToggleButtonGroup>
                        <Autocomplete
                          key={`${v.key}-${source}`}
                          options={(() => {
                            if (source !== 'resource') return options;
                            // Sort resources: same group as suggestion first, then the rest
                            const related = envRelatedResources[v.key];
                            if (!related || related.length === 0) return options;
                            return [...options].sort((a, b) => {
                              const aRelated = related.includes(a.ref) ? 0 : 1;
                              const bRelated = related.includes(b.ref) ? 0 : 1;
                              if (aRelated !== bRelated) return aRelated - bRelated;
                              return a.name.localeCompare(b.name);
                            });
                          })()}
                          getOptionLabel={option => `${option.name} (${option.type})`}
                          value={options.find(r => r.ref === envValues[v.key]) ?? null}
                          onChange={(_e, newValue) =>
                            setEnvValues(prev => ({ ...prev, [v.key]: newValue?.ref ?? '' }))
                          }
                          onInputChange={(_e, inputValue, reason) => {
                            if (source === 'component' && reason === 'input') {
                              searchComponents(inputValue);
                            }
                          }}
                          loading={source === 'component' && componentLoading}
                          noOptionsText={
                            source === 'component'
                              ? 'Digite ao menos 2 caracteres para buscar'
                              : 'Nenhum recurso encontrado'
                          }
                          renderOption={(option) => {
                            const related = envRelatedResources[v.key] ?? [];
                            const isRelated = source === 'resource' && related.includes(option.ref);
                            return (
                              <div style={{
                                width: '100%',
                                margin: '-6px -16px',
                                padding: '6px 16px',
                                backgroundColor: isRelated ? 'rgba(25, 118, 210, 0.06)' : undefined,
                              }}>
                                {option.name} ({option.type})
                              </div>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              placeholder={source === 'component' ? 'Buscar componente...' : 'Selecione um recurso'}
                              fullWidth
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {source === 'component' && componentLoading && (
                                      <CircularProgress size={18} />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          fullWidth
                          size="small"
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEnvVars(prev => prev.filter(item => item.key !== v.key));
                            setEnvValues(prev => {
                              const next = { ...prev };
                              delete next[v.key];
                              return next;
                            });
                            setEnvDepSource(prev => {
                              const next = { ...prev };
                              delete next[v.key];
                              return next;
                            });
                          }}
                          title="Remover"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    );
                  })}
                </Box>
              </Paper>
            )}

            {groupProjects.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom style={{ marginTop: 24 }}>
                  Dependências — {groupName}
                </Typography>
                {groupProjects.map(p => {
                  const projectKey = p.project.path_with_namespace;
                  const projectVals = groupValues[projectKey] ?? {};
                  const projectSources = groupDepSource[projectKey] ?? {};
                  const projectRelated = groupRelatedResources[projectKey] ?? {};
                  // Format: subgroup (repo) — e.g. "seif-ia (web)"
                  const segments = projectKey.split('/');
                  const repoName = segments[segments.length - 1];
                  const subGroup = segments.length >= 3 ? segments[segments.length - 2] : '';
                  const projectLabel = subGroup ? `${subGroup} (${repoName})` : repoName;

                  return (
                    <Paper key={projectKey} className={classes.envPaper}>
                      <Typography variant="subtitle1" className={classes.projectHeader}>
                        {projectLabel}
                        <IconButton
                          size="small"
                          href={`https://git.fiesc.com.br/${projectKey}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={projectKey}
                          style={{ marginLeft: 8 }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Typography>
                      {p.envVars.map(v => {
                        const source = projectSources[v.key] ?? 'resource';
                        const options = source === 'resource' ? resources : componentOptions;
                        return (
                          <div key={`${projectKey}:${v.key}`} className={classes.envRow}>
                            <Typography className={classes.envLabel}>{v.key}</Typography>
                            <ToggleButtonGroup
                              value={source}
                              exclusive
                              onChange={(_e, val) => {
                                if (val) {
                                  setGroupDepSource(prev => ({
                                    ...prev,
                                    [projectKey]: { ...prev[projectKey], [v.key]: val },
                                  }));
                                  setGroupValues(prev => ({
                                    ...prev,
                                    [projectKey]: { ...prev[projectKey], [v.key]: '' },
                                  }));
                                }
                              }}
                              size="small"
                            >
                              <ToggleButton value="component">Comp</ToggleButton>
                              <ToggleButton value="resource">Res</ToggleButton>
                            </ToggleButtonGroup>
                            <Autocomplete
                              key={`${projectKey}:${v.key}-${source}`}
                              options={(() => {
                                if (source !== 'resource') return options;
                                const related = projectRelated[v.key];
                                if (!related || related.length === 0) return options;
                                return [...options].sort((a, b) => {
                                  const aRelated = related.includes(a.ref) ? 0 : 1;
                                  const bRelated = related.includes(b.ref) ? 0 : 1;
                                  if (aRelated !== bRelated) return aRelated - bRelated;
                                  return a.name.localeCompare(b.name);
                                });
                              })()}
                              getOptionLabel={option => `${option.name} (${option.type})`}
                              value={options.find(r => r.ref === projectVals[v.key]) ?? null}
                              onChange={(_e, newValue) =>
                                setGroupValues(prev => ({
                                  ...prev,
                                  [projectKey]: { ...prev[projectKey], [v.key]: newValue?.ref ?? '' },
                                }))
                              }
                              onInputChange={(_e, inputValue, reason) => {
                                if (source === 'component' && reason === 'input') {
                                  searchComponents(inputValue);
                                }
                              }}
                              loading={source === 'component' && componentLoading}
                              noOptionsText={
                                source === 'component'
                                  ? 'Digite ao menos 2 caracteres para buscar'
                                  : 'Nenhum recurso encontrado'
                              }
                              renderOption={(option) => {
                                const related = projectRelated[v.key] ?? [];
                                const isRelated = source === 'resource' && related.includes(option.ref);
                                return (
                                  <div style={{
                                    width: '100%',
                                    margin: '-6px -16px',
                                    padding: '6px 16px',
                                    backgroundColor: isRelated ? 'rgba(25, 118, 210, 0.06)' : undefined,
                                  }}>
                                    {option.name} ({option.type})
                                  </div>
                                );
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  size="small"
                                  placeholder={source === 'component' ? 'Buscar componente...' : 'Selecione um recurso'}
                                  fullWidth
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {source === 'component' && componentLoading && (
                                          <CircularProgress size={18} />
                                        )}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              fullWidth
                              size="small"
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                setGroupProjects(prev =>
                                  prev.map(gp =>
                                    gp.project.path_with_namespace === projectKey
                                      ? { ...gp, envVars: gp.envVars.filter(item => item.key !== v.key) }
                                      : gp,
                                  ),
                                );
                                setGroupValues(prev => {
                                  const next = { ...prev[projectKey] };
                                  delete next[v.key];
                                  return { ...prev, [projectKey]: next };
                                });
                              }}
                              title="Remover"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </div>
                        );
                      })}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}
